import React from 'react';
import * as Yup from 'yup';
import { Formik, Field, ErrorMessage } from 'formik';
import BixButton from '$/components/global/bixButton';
import InputBlock from '$/components/global/inputBlock';
import BixToggle from '$/components/global/bixToggle';
import * as Bixby from '$/core';
import './index.scss';

interface IUserProfileFormProps {
  industries: Bixby.Models.IIdentifiable[] | undefined;
  titles: Bixby.Models.IIdentifiable[] | undefined;
  currentUser?: any;
  initialUserValues: UpdateUserValues;
  isCurrentUserBixbyComplianceAdmin: boolean;
  isSelectedUserBixbyComplianceAdmin: boolean;
  isSelectedUserAdmin: boolean;
  isSelectedUserClient: boolean;
  isUpdating?: boolean;
  hasBixbyComplianceFeatureFlag: boolean;
  updateUser: (values: Bixby.Models.UpdateUserRequest) => Promise<void>;
  resendEmail: () => Promise<void>;
}

export interface UpdateUserValues {
  id: string;
  companies: Bixby.Models.UserCompany[];
  firstName: string;
  industry_Id: string;
  lastName: string;
  title_Id: string;
  notifyOnCompanyAddition: boolean;
  isCompanyMNPIInfoAdmin?: boolean;
  isClientComplianceAdmin?: boolean;
  portfolioManagerLevel?: number;
}

const UserProfileForm: React.FC<IUserProfileFormProps> = props => {
  const {
    industries,
    titles,
    currentUser,
    initialUserValues,
    updateUser,
    isCurrentUserBixbyComplianceAdmin,
    isSelectedUserBixbyComplianceAdmin,
    isSelectedUserAdmin,
    isSelectedUserClient,
    isUpdating,
    hasBixbyComplianceFeatureFlag,
    resendEmail,
  } = props;
  const industryOptions = (industries || []).map(industry => {
    return (
      <option value={industry.id} key={industry.id}>
        {industry.name}
      </option>
    );
  });

  const titleOptions = (titles || []).map(title => {
    return (
      <option value={title.id} key={title.id}>
        {title.name}
      </option>
    );
  });

  const schema = Yup.object({
    firstName: Yup.string().max(256, 'Too long').required('First name is required.'),
    lastName: Yup.string().max(256, 'Too long').required('Last name is required.'),
  });

  return currentUser && !Bixby.Utils.isNullOrEmpty(initialUserValues) ? (
    <React.Fragment>
      <section className="user_edit_form">
        <Formik
          initialValues={initialUserValues}
          validationSchema={schema}
          onSubmit={(values: UpdateUserValues) => {
            updateUser(values);
          }}
          render={({ handleSubmit, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="profile-input-container">
                <div className="profile-input-column">
                  <InputBlock title="First Name*">
                    <Field disabled={isUpdating} type="text" name="firstName" />
                  </InputBlock>
                  <ErrorMessage name="firstName">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>

                  <InputBlock title="Last Name*">
                    <Field disabled={isUpdating} type="text" name="lastName" />
                  </InputBlock>
                  <ErrorMessage name="lastName">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>

                  <InputBlock title="User ID*">
                    <input name="email" type="text" disabled={true} value={currentUser.email} />
                  </InputBlock>

                  <InputBlock title="Industry">
                    <Field name="industry_Id" component="select" disabled={isSelectedUserAdmin || isUpdating}>
                      <option value="" key="-1" disabled>
                        {isSelectedUserAdmin ? 'Bixby Admin User' : 'Select Industry'}
                      </option>
                      {industryOptions}
                    </Field>
                  </InputBlock>
                  <ErrorMessage name="industry_Id">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                  <InputBlock title="Title">
                    <Field name="title_Id" component="select" disabled={isSelectedUserAdmin || isUpdating}>
                      <option value="" key="-1" disabled>
                        {isSelectedUserAdmin ? 'Bixby Admin User' : 'Select Title'}
                      </option>
                      {titleOptions}
                    </Field>
                  </InputBlock>
                  <ErrorMessage name="title_Id">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                </div>
                <div className="profile-input-column">
                  {isSelectedUserAdmin === false && (
                    <>
                      <InputBlock title="Send e-mail on company addition">
                        <BixToggle
                          disabled={isUpdating}
                          id="notifyOnCompanyAddition"
                          checked={values.notifyOnCompanyAddition}
                          onChange={v => {
                            setFieldValue('notifyOnCompanyAddition', v);
                          }}
                        />
                      </InputBlock>
                      <ErrorMessage name="notifyOnCompanyAddition">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                    </>
                  )}
                  {isSelectedUserAdmin && !isSelectedUserBixbyComplianceAdmin && isCurrentUserBixbyComplianceAdmin && (
                    <>
                      <InputBlock title="Bixby Admin Manages Private Data (MNPI)">
                        <BixToggle
                          disabled={isUpdating}
                          id="isCompanyMNPIInfoAdmin"
                          checked={values.isCompanyMNPIInfoAdmin}
                          onChange={v => {
                            setFieldValue('isCompanyMNPIInfoAdmin', v);
                          }}
                        />
                      </InputBlock>
                      <ErrorMessage name="isCompanyMNPIInfoAdmin">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                    </>
                  )}
                  {hasBixbyComplianceFeatureFlag &&
                    isSelectedUserClient &&
                    isCurrentUserBixbyComplianceAdmin &&
                    currentUser.clientHasBixbyCompliance && (
                      <>
                        <InputBlock title="User is a Client Compliance Admin">
                          <BixToggle
                            text={
                              currentUser.isPrimaryClientComplianceAdmin
                                ? 'Cannot update primary CCA on this screen. Please update on client profile.'
                                : ''
                            }
                            disabled={
                              isUpdating ||
                              currentUser.isPrimaryClientComplianceAdmin ||
                              (currentUser.portfolioManagerLevel !== undefined && values.portfolioManagerLevel !== null)
                            }
                            id="isClientComplianceAdmin"
                            checked={values.isClientComplianceAdmin}
                            onChange={v => {
                              setFieldValue('isClientComplianceAdmin', v);
                            }}
                          />
                        </InputBlock>
                        <ErrorMessage name="isClientComplianceAdmin">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                      </>
                    )}
                  {hasBixbyComplianceFeatureFlag &&
                    isSelectedUserClient &&
                    currentUser.clientHasBixbyCompliance &&
                    currentUser.clientHasBixbyCompliance &&
                    currentUser.clientHasPortfolioManagerWorkflow && (
                      <InputBlock title="Portfolio Manager Level">
                        <select
                          name="portfolioManagerLevel"
                          disabled={
                            isUpdating || currentUser.isPrimaryClientComplianceAdmin || !!values.isClientComplianceAdmin
                          }
                          value={values.portfolioManagerLevel ?? ''}
                          onChange={e => {
                            const v = e.target.value;
                            setFieldValue('portfolioManagerLevel', v !== '' ? parseInt(v, 10) : undefined);
                          }}>
                          <option value="">None</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                        </select>
                      </InputBlock>
                    )}
                  {!currentUser.isActive && (
                    <BixButton onClick={resendEmail} disabled={isUpdating} useLoading isLoading={isUpdating}>
                      Resend Invite Email
                    </BixButton>
                  )}
                </div>
              </div>
              <div className="button_container">
                <BixButton type="submit" disabled={isUpdating} useLoading isLoading={isUpdating}>
                  Save User
                </BixButton>
              </div>
            </form>
          )}></Formik>
      </section>
    </React.Fragment>
  ) : null;
};

export default UserProfileForm;
